import type { UpdateOrderStatusParams } from '@/entities/order/model'
import { Badge, Button, Popover, Radio, Stack, Text } from '@mantine/core'
import { useMemo, useState } from 'react'
import { useOrderStatusMutation } from '@/entities/order/model'
import { deriveStatusOptions, useStateConfigQuery } from '@/entities/state/model'
import { useOrderGroupMembers } from '@/features/order/lib'
import { RadioIconCheck } from '@/shared/ui/radio-icon-check'
import classes from './StatusUpdatePopover.module.css'

interface StatusUpdatePopoverProps {
  orderId: string
  currentStatus: string
  lastUpdateTime?: string
  onStatusUpdate?: (status: string) => void
  /**
   * Optional custom trigger render function
   */
  renderTrigger?: (props: { onClick: () => void, isOpen: boolean }) => React.ReactNode
}

export function StatusUpdatePopover({
  orderId,
  currentStatus,
  lastUpdateTime,
  onStatusUpdate,
  renderTrigger,
}: StatusUpdatePopoverProps) {
  const [opened, setOpened] = useState(false)
  const [selectedStatus, setSelectedStatus] = useState<string>(currentStatus)
  const [isUpdating, setIsUpdating] = useState(false)

  function handleOpenChange(isOpen: boolean) {
    setOpened(isOpen)
    if (!isOpen) {
      // Reset to current status when closing without updating
      setSelectedStatus(currentStatus)
    }
  }

  const { data, isLoading: isLoadingStates } = useStateConfigQuery()
  const { mutateAsync: updateStatusMutation, isPending } = useOrderStatusMutation()
  const groupMemberOrderIds = useOrderGroupMembers(orderId)

  const statusOptions = useMemo(() => {
    return deriveStatusOptions(data?.states)
  }, [data])

  /**
   * Handles the status update
   */
  async function handleStatusUpdate(newStatus: string) {
    if (!newStatus || !orderId || newStatus === currentStatus)
      return

    setIsUpdating(true)

    try {
      const updatePromises = groupMemberOrderIds.map((memberId) => {
        const updateParams: UpdateOrderStatusParams = {
          orderId: memberId,
          newStatus,
          lastUpdateTime,
        }
        return updateStatusMutation(updateParams)
      })

      await Promise.all(updatePromises)

      if (onStatusUpdate) {
        onStatusUpdate(newStatus)
      }

      setOpened(false)
    } catch (error) {
      console.error('Failed to update status:', error)
    } finally {
      setIsUpdating(false)
    }
  }

  const currentStatusDisplay = statusOptions.find(option => option.value === currentStatus)?.label || currentStatus

  return (
    <Popover
      opened={opened}
      onChange={handleOpenChange}
      position="bottom-start"
      width={250}
      radius="md"
      shadow="md"
      withArrow
    >
      <Popover.Target>
        {renderTrigger ? (
          renderTrigger({
            onClick: () => setOpened(o => !o),
            isOpen: opened,
          })
        ) : (
          <Badge
            color="gray.8"
            radius="sm"
            size="lg"
            style={{ cursor: 'pointer' }}
            onClick={() => setOpened(o => !o)}
          >
            {currentStatusDisplay}
          </Badge>
        )}
      </Popover.Target>
      <Popover.Dropdown>
        <Stack>
          {isLoadingStates ? (
            <Text>Loading available statuses...</Text>
          ) : statusOptions.length === 0 ? (
            <Text>No status transitions available</Text>
          ) : (
            <Radio.Group
              value={selectedStatus}
              onChange={setSelectedStatus}
              name="orderStatus"
            >
              {statusOptions.map(option => (
                <Radio
                  classNames={{
                    radio: classes.radio,
                    body: classes.body,
                    icon: classes.icon,
                    inner: classes.inner,
                    label: classes.label,
                    labelWrapper: classes.labelWrapper,
                  }}
                  icon={RadioIconCheck}
                  key={option.value}
                  value={option.value}
                  label={option.label}
                  labelPosition="left"
                  size="lg"
                  py="8px"
                  disabled={isUpdating || isPending}
                />
              ))}
            </Radio.Group>
          )}

          <Button
            variant="filled"
            color="gray.8"
            fullWidth
            size="sm"
            radius="md"
            onClick={() => handleStatusUpdate(selectedStatus)}
            loading={isUpdating || isPending}
            disabled={selectedStatus === currentStatus
              || isUpdating
              || isPending
              || !selectedStatus
              || statusOptions.length === 0}
          >
            Update
          </Button>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  )
}
